import { Box, Divider, Typography, Skeleton } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import zeplinColor from 'theme/Color';
import dayjs from 'dayjs';
import { DealsService } from 'service/deals.service';
import { Utilities } from 'utilities/utilities';

type PriceBookProductsProps = {
  dealData: any;
  loading: boolean;
};

export const PriceBookProducts = ({ dealData, loading }: PriceBookProductsProps) => {
  const [enrichedProducts, setEnrichedProducts] = useState<any[]>([]);
  const [fetchingNames, setFetchingNames] = useState(false);
  const [pricebookName, setPricebookName] = useState<string>('N/A');
  const [pricebooks, setPricebooks] = useState<any>({});
  
  const rawProducts = dealData?.products || dealData?.dealDetails?.products || [];
  const pricebookId = dealData?.priceBookId || dealData?.pricebook2Id || dealData?.pricebookId;

  useEffect(() => {
    const fetchPricebookName = async () => {
      if (!pricebookId || pricebookId === 'none') {
        setPricebookName('N/A');
        return;
      }

      try {
        const res = await DealsService.getPricebooks();
        setPricebooks(res);
        const name = Utilities.getMatchingRecordTypeKey(pricebookId, res);
        setPricebookName(name || 'N/A');
      } catch (error) {
        console.error('Failed to fetch pricebook name:', error);
        setPricebookName('N/A');
      }
    };

    fetchPricebookName();
  }, [pricebookId]);

  // Check if products have names
  const productsHaveNames = useMemo(() => {
    return rawProducts.some((p: any) => p?.ProductName || p?.productName || p?.name || p?.Name);
  }, [rawProducts]);

  // Fetch product names from pricebook if missing
  useEffect(() => {
    if (!rawProducts || rawProducts.length === 0) {
      setEnrichedProducts([]);
      return;
    }

    // If products already have names, use them directly
    if (productsHaveNames) {
      setEnrichedProducts(rawProducts);
      return;
    }

    // If we don't have a pricebook ID, can't fetch names
    if (!pricebookId || pricebookId === 'none') {
      setEnrichedProducts(rawProducts);
      return;
    }

    // Fetch original products to get names
    const fetchProductNames = async () => {
      try {
        setFetchingNames(true);
        const res = await DealsService.getPricebookProducts(pricebookId);
        const productsList = res?.products || res || [];

        // Merge saved products with original products by PricebookEntryId
        const merged = rawProducts.map((entry: any) => {
          const originalProduct = productsList.find(
            (p: any) => (p?.PricebookEntryId || p?.id) === (entry?.PricebookEntryId || entry?.id)
          );
          
          if (originalProduct) {
            return {
              ...originalProduct,
              ...entry,
              // Preserve name from original product
              name: originalProduct?.name || originalProduct?.ProductName || entry?.name || entry?.ProductName,
              ProductName: originalProduct?.ProductName || originalProduct?.name || entry?.ProductName || entry?.name,
            };
          }
          return entry;
        });

        setEnrichedProducts(merged);
      } catch (error) {
        console.error('Failed to fetch product names:', error);
        setEnrichedProducts(rawProducts);
      } finally {
        setFetchingNames(false);
      }
    };

    fetchProductNames();
  }, [rawProducts, pricebookId, productsHaveNames]);

  const products = enrichedProducts.length > 0 ? enrichedProducts : rawProducts;

  if (loading || fetchingNames) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', pb: 2, flex: 1, overflow: 'scroll', pr: 1, width: '100%' }}>
        <Skeleton variant='text' width={150} height={32} />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}

        <Divider sx={{ my: 3 }} />

        <Skeleton variant='text' width={200} height={32} />
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}
      </Box>
    );
  }

  if (!products || products.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '32px',
          py: 8,
          textAlign: 'center',
        }}
      >
        <img src='/assets/no-pricebook.svg' alt='No pricebook' style={{ width: '120px', height: '120px', opacity: 0.5 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography variant='heading3' fontWeight='600'>
            No Products Added Yet
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Start by selecting a price book to add products
            <br /> and set pricing for this deal
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', pb: 2, flex: 1, overflow: 'scroll', pr: 1, width: '100%' }}>
      {/* Price Book Section */}
      <Box>
        <Typography variant='heading3' fontWeight='bold' sx={{ color: zeplinColor.BasicBlack, mb: 3 }}>
          Price Book
        </Typography>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <img
            src='/assets/price-book.svg'
            alt='price book'
            style={{ width: '20px', height: '20px', objectFit: 'fill' }}
          />
          <Typography variant='body2' sx={{ width: 180, color: zeplinColor.Neutral8, flexShrink: 0 }}>
            Price book
          </Typography>
          <Typography variant='body2' sx={{ flexGrow: 1 }}>
            {pricebookName}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Products Section */}
      <Box>
        <Typography variant='heading3' fontWeight='bold' sx={{ color: zeplinColor.BasicBlack, mb: 3 }}>
          Products ({products.length})
        </Typography>

        {products.map((product: any, index: number) => (
          <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: '24px', mb: 3 }}>
            {/* Product Name */}
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <img
                src='/assets/product-name.svg'
                alt='product name'
                style={{ width: '20px', height: '20px', objectFit: 'fill' }}
              />
              <Typography variant='body2' sx={{ width: 180, color: zeplinColor.Neutral8, flexShrink: 0 }}>
                Product name
              </Typography>
              <Typography variant='body2' sx={{ flexGrow: 1 }}>
                {product?.ProductName || product?.productName || product?.name || product?.Name || product?.product?.name || product?.product?.ProductName || 'N/A'}
              </Typography>
            </Box>

            {/* Product Family */}
            {product?.ProductFamily && (
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <img
                  src='/assets/deal.svg'
                  alt='product family'
                  style={{ width: '20px', height: '20px', objectFit: 'fill' }}
                />
                <Typography variant='body2' sx={{ width: 180, color: zeplinColor.Neutral8, flexShrink: 0 }}>
                  Product family
                </Typography>
                <Typography variant='body2' sx={{ flexGrow: 1 }}>
                  {product.ProductFamily}
                </Typography>
              </Box>
            )}

            {/* Sales Price */}
            {(product?.SalesPrice || product?.UnitPrice) && (
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <img
                  src='/assets/amount-new.svg'
                  alt='sales price'
                  style={{ width: '20px', height: '20px', objectFit: 'fill' }}
                />
                <Typography variant='body2' sx={{ width: 180, color: zeplinColor.Neutral8, flexShrink: 0 }}>
                  Sales price
                </Typography>
                <Typography variant='body2' sx={{ flexGrow: 1 }}>
                  {product.SalesPrice || product.UnitPrice ? `$${product.SalesPrice || product.UnitPrice}` : 'N/A'}
                </Typography>
              </Box>
            )}

            {/* Quantity */}
            {product?.Quantity && (
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <img
                  src='/assets/quantity.svg'
                  alt='quantity'
                  style={{ width: '20px', height: '20px', objectFit: 'fill' }}
                />
                <Typography variant='body2' sx={{ width: 180, color: zeplinColor.Neutral8, flexShrink: 0 }}>
                  Quantity
                </Typography>
                <Typography variant='body2' sx={{ flexGrow: 1 }}>
                  {product.Quantity}
                </Typography>
              </Box>
            )}

            {/* Date */}
            {(product?.Date || product?.ServiceDate) && (
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <img
                  src='/assets/calendar-fade.svg'
                  alt='date'
                  style={{ width: '20px', height: '20px', objectFit: 'fill' }}
                />
                <Typography variant='body2' sx={{ width: 180, color: zeplinColor.Neutral8, flexShrink: 0 }}>
                  Date
                </Typography>
                <Typography variant='body2' sx={{ flexGrow: 1 }}>
                  {product.Date || (product.ServiceDate ? dayjs(product.ServiceDate).format('MM/DD/YYYY') : 'N/A')}
                </Typography>
              </Box>
            )}

            {/* List Price */}
            {product?.ListPrice && (
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <img
                  src='/assets/amount-new.svg'
                  alt='list price'
                  style={{ width: '20px', height: '20px', objectFit: 'fill' }}
                />
                <Typography variant='body2' sx={{ width: 180, color: zeplinColor.Neutral8, flexShrink: 0 }}>
                  List price
                </Typography>
                <Typography variant='body2' sx={{ flexGrow: 1 }}>
                  ${product.ListPrice}
                </Typography>
              </Box>
            )}

            {index < products.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
